import type { MessageSendMode, UIInteraction } from '@web/api/types'
import {
  humanizeKey,
  StructuredValue,
} from '@web/components/tools/StructuredContent'
import { Icon } from '@web/components/ui/icons'

export type MessageOrigin = string | { kind?: string } | null

export function originKind(origin?: MessageOrigin): string | undefined {
  return typeof origin === 'string' ? origin : origin?.kind
}

export function systemOrigin(origin?: MessageOrigin): boolean {
  return ['system', 'schedule', 'scheduled', 'cron', 'app-upgrade'].includes(
    originKind(origin) ?? '',
  )
}

export function originLabel(origin?: MessageOrigin): string {
  const kind = originKind(origin) ?? ''
  if (['app-discussion', 'ui', 'app'].includes(kind)) return 'App action'
  if (['schedule', 'scheduled', 'cron'].includes(kind)) return 'Scheduled task'
  if (kind === 'app-upgrade') return 'App updated'
  return humanizeKey(kind || 'System')
}

/** Explain special send circumstances beneath the message without adding transcript noise. */
export function MessageSendCaption({
  mode,
  origin,
  synthetic,
}: {
  mode?: MessageSendMode
  origin?: MessageOrigin
  synthetic?: boolean
}) {
  const labels: Record<MessageSendMode, string> = {
    normal: '',
    queued: 'Queued Message',
    steer: 'Steered Conversation',
    send_now: 'Sent Immediately',
    ui: 'Sent from App',
    scheduled: 'Scheduled Message',
    system: 'Injected Context',
  }
  const kind = originKind(origin)
  const label =
    (mode && labels[mode]) ||
    (synthetic
      ? 'Injected Context'
      : kind && kind !== 'user'
        ? originLabel(origin)
        : '')
  return label ? (
    <p
      className="message-send-caption mt-1 px-1 text-right text-[10.5px] leading-4 text-muted"
      data-send-mode={mode}
    >
      {label}
    </p>
  ) : null
}

/** One durable interaction labels its user message; model-only context stays outside this view. */
export function MessageInteraction({
  interaction,
  origin,
  synthetic,
  accent = false,
}: {
  interaction?: UIInteraction | null
  origin?: MessageOrigin
  synthetic?: boolean
  accent?: boolean
}) {
  const kind = originKind(origin)
  if (!interaction && (!kind || kind === 'user') && !synthetic) return null
  const label =
    interaction?.label ?? (synthetic ? 'Injected context' : originLabel(origin))
  return (
    <div
      className="message-interaction mb-2 border-b border-line pb-2"
      data-interaction-id={interaction?.id}
      data-accent={accent}
    >
      <div
        className={`mb-1 flex items-center gap-1.5 text-[11px] font-semibold ${accent ? 'text-accent-fg' : 'text-muted'}`}
      >
        <Icon name={systemOrigin(origin) ? 'clock' : 'panelRight'} size={12} />
        <span>{label}</span>
      </div>
      {!!interaction?.fields.length && (
        <dl>
          {interaction.fields.map((field, index) => (
            <div key={index}>
              <dt
                className={`text-[10px] ${accent ? 'text-accent-fg' : 'text-muted'}`}
              >
                {field.label}
              </dt>
              <dd className="text-[12px]">
                <StructuredValue value={field.value} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
